import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { URLS } from '../../../shared/constants/urls.constant';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingCanvasCategoryEnum,
  TrackingCanvasTypeEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { SetupBankTransferDialogBuilder } from './setup-bank-transfer-dialog.builder';
import { AchDebit } from '../../../settings-data/sub-models/company-settings/ach-debit.model';
import { NavigationService } from '../../../navigation/navigation.service';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';

export const DIALOG_TYPE = 'setup-bank-transfer';

@Component({
  selector: 'app-setup-bank-transfer-dialog',
  templateUrl: './setup-bank-transfer-dialog.component.html',
  styleUrls: ['./setup-bank-transfer-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [SetupBankTransferDialogBuilder],
})
export class SetupBankTransferDialogComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() achDebit: AchDebit;
  @Input() underwritingUrl: string;
  @Input() locale: string;
  @Input() canvasIdentifier: string;
  @Input() trackingScreenName: TrackingScreenNamesEnum;

  @Output() onConfirm: EventEmitter<void> = new EventEmitter<void>();
  @Output() onViewMore: EventEmitter<void> = new EventEmitter<void>();
  @Output() onCancel: EventEmitter<void> = new EventEmitter<void>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();

  dialogType: string;
  headerLabel: string;
  description: string;
  confirmLabel: string;
  confirmUrl: string;
  learnMoreLabel: string;
  trackingObject: ITrackingObject;
  linkBankAccountTapped: ITrackingAction;
  viewInformationTapped: ITrackingAction;

  constructor(
    private _translateService: TranslateService,
    private _navigationService: NavigationService,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _setupBankTransferDialogBuilder: SetupBankTransferDialogBuilder
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.underwritingUrl) {
      this.confirmUrl = this.underwritingUrl || URLS.PAYMENTS_STRIPE_DASHBOARD;
    }

    if (changes.achDebit && changes.locale && this.achDebit) {
      this.description = this._setupBankTransferDialogBuilder.buildDescription(
        this.achDebit,
        this.locale
      );
    }
  }

  ngOnInit() {
    this.dialogType = DIALOG_TYPE;
    this.headerLabel = this._translateService.instant(
      'DIALOG.PAYMENTS.HEADER.SETUP_BANK_TRANSFER'
    );
    this.learnMoreLabel = this._translateService.instant('ACTION.LEARN_MORE');
    this.confirmLabel = this._translateService.instant('ACTION.SETUP_NOW');
    this.trackingObject = this._trackingObjectBuilder.buildCanvas(
      this.canvasIdentifier,
      TrackingCanvasTypeEnum.payments,
      TrackingCanvasCategoryEnum.action,
      'link_bank_account',
      'dismiss'
    );
    this.linkBankAccountTapped = this._trackingActionBuilder.buildButtonTapped(
      'link_bank_account',
      this.trackingScreenName
    );
    this.viewInformationTapped = this._trackingActionBuilder.buildButtonTapped(
      'view_information',
      this.trackingScreenName
    );
  }

  onViewMoreClick() {
    this.onViewMore.emit();

    this._navigationService.goToExternalUrl(URLS.PAYMENTS_BANK_TRANSFER, true);
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
